import React from "react";
import { formatDistanceToNow } from "date-fns";
import { Button } from "@/components/ui/button";
import { GeneratedElfLetter } from "@/types/elf-letter";
import { ScrollArea } from "@/components/ui/scroll-area";

interface LetterListProps {
  letters: GeneratedElfLetter[];
  activeLetterId: string | null;
  onSelect: (letterId: string) => void;
  onReset: () => void;
  disabled?: boolean;
}

const LetterList: React.FC<LetterListProps> = ({
  letters,
  activeLetterId,
  onSelect,
  onReset,
  disabled = false,
}) => {
  if (!letters.length) {
    return (
      <div className="rounded-2xl border border-dashed border-muted bg-muted/30 p-6 text-center text-sm text-muted-foreground">
        Your generated letters will appear here. Create one to get started!
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-foreground uppercase tracking-wide">
          Session Letters
        </h3>
        <Button
          variant="ghost"
          size="sm"
          onClick={onReset}
          disabled={disabled}
          className="text-xs text-muted-foreground hover:text-foreground focus-visible:ring-slate-700"
        >
          Clear All
        </Button>
      </div>

      <ScrollArea className="h-[320px] rounded-2xl border border-muted bg-white">
        <ul className="divide-y divide-muted/40">
          {letters.map((letter) => {
            const relativeTime = formatDistanceToNow(new Date(letter.createdAt), {
              addSuffix: true,
            });
            const snippet = letter.content.replace(/\s+/g, " ").slice(0, 96);
            const isActive = letter.letterId === activeLetterId;

            return (
              <li key={letter.letterId}>
                <button
                  type="button"
                  onClick={() => onSelect(letter.letterId)}
                  disabled={disabled}
                  className={`group w-full text-left px-4 py-3 transition focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-slate-700 disabled:pointer-events-none disabled:opacity-50 ${
                    isActive
                      ? "bg-elfGreen/10 border-l-4 border-elfGreen text-foreground"
                      : "hover:bg-muted/60 text-muted-foreground"
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-semibold text-foreground">
                      {letter.childName}
                    </span>
                    <span className="text-xs uppercase tracking-wide text-muted-foreground group-hover:text-foreground">
                      {relativeTime}
                    </span>
                  </div>
                  <p className="mt-1 text-xs leading-5 group-hover:text-foreground">
                    {snippet}
                    {letter.content.length > 96 && "..."}
                  </p>
                </button>
              </li>
            );
          })}
        </ul>
      </ScrollArea>
    </div>
  );
};

export default LetterList;

